import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useMemo } from 'react'

import {
  getTikTokActionCategories,
  clearTikTokActionCategories,
  getTikTokRegions,
  clearTikTokRegions,
  clearTikTokCustomAudiences,
  getTikTokCustomAudiences,
  getTikTokInterests,
  clearTikTokInterests,
  getAdditionalInterestCategoriesByIds,
  getPurchaseIntentionCategoriesByIds
} from '../../../../modules/actions/tikTokConstants'
import { selectedTikTokAdAccountSelector } from '../../../../modules/selectors/adAccounts'
import {
  getAdditionalInterestCategoriesByIdsWasLoadedSelector,
  getPurchaseIntentionCategoriesByIdsWasLoadedSelector,
  tikTokActionCategoriesErrorSelector,
  tikTokActionCategoriesWasLoadedSelector,
  tikTokCustomAudiencesErrorSelector,
  tikTokCustomAudiencesWasLoadedSelector,
  tikTokInterestsErrorSelector,
  tikTokInterestsWasLoadedSelector,
  tikTokRegionsErrorSelector,
  tikTokRegionsWasLoadedSelector
} from '../../../../modules/selectors/tikTokConstants'
import { campaignSelector } from '../../../../modules/selectors/campaigns'
import { INTEREST_KEYWORD_IDS } from '../../UploadTikTokAdCreative/UploadTikTokAdCreativeForm/LineItemForm/Steps/CustomAudiencesStep/TikTokUploadCreativeAudienceFields/TikTokAdvancedAudienceTargetingSection/AdditionalInterestCategoriesMultiselect'
import { lineItemSelector } from '../../../../modules/selectors/lineItems'
import { PURCHASE_INTENTION_KEYWORD_IDS } from '../../UploadTikTokAdCreative/UploadTikTokAdCreativeForm/LineItemForm/Steps/CustomAudiencesStep/TikTokUploadCreativeAudienceFields/TikTokAdvancedAudienceTargetingSection/PurchaseIntentionCategoriesMultiselect'

export function useLoadAdditionalData({ isFormOpen }) {
  const dispatch = useDispatch()

  const { id: tiktokAdAccountId } = useSelector(selectedTikTokAdAccountSelector)
  const campaign = useSelector(campaignSelector)
  const lineItem = useSelector(lineItemSelector)
  const tikTokRegionsWasLoaded = useSelector(tikTokRegionsWasLoadedSelector)
  const tikTokRegionsError = useSelector(tikTokRegionsErrorSelector)
  const tikTokAudiencesWasLoaded = useSelector(tikTokCustomAudiencesWasLoadedSelector)
  const tikTokAudiencesError = useSelector(tikTokCustomAudiencesErrorSelector)
  const tikTokActionCategoriesWasLoaded = useSelector(tikTokActionCategoriesWasLoadedSelector)
  const tikTokActionCategoriesError = useSelector(tikTokActionCategoriesErrorSelector)
  const tikTokInterestsWasLoaded = useSelector(tikTokInterestsWasLoadedSelector)
  const tikTokInterestsError = useSelector(tikTokInterestsErrorSelector)

  const getAdditionalInterestCategoriesByIdsWasLoaded = useSelector(
    getAdditionalInterestCategoriesByIdsWasLoadedSelector
  )
  const getPurchaseIntentionCategoriesByIdsWasLoaded = useSelector(getPurchaseIntentionCategoriesByIdsWasLoadedSelector)

  const { objective: campaignObjective } = campaign

  const tikTokRegionsDidLoading = tikTokRegionsWasLoaded || tikTokRegionsError
  const tikTokAudiencesDidLoading = tikTokAudiencesWasLoaded || tikTokAudiencesError
  const tikTokActionCategoriesDidLoading = tikTokActionCategoriesWasLoaded || tikTokActionCategoriesError
  const tikTokInterestsDidLoading = tikTokInterestsWasLoaded || tikTokInterestsError

  const additionalInterestCategoriesIds = useMemo(() => {
    return lineItem[INTEREST_KEYWORD_IDS] || []
  }, [lineItem])

  const purchaseIntentionCategoriesIds = useMemo(() => {
    return lineItem[PURCHASE_INTENTION_KEYWORD_IDS] || []
  }, [lineItem])

  const isAdditionalDataLoading =
    !tikTokRegionsDidLoading ||
    !tikTokAudiencesDidLoading ||
    !tikTokActionCategoriesDidLoading ||
    !tikTokInterestsDidLoading ||
    (additionalInterestCategoriesIds.length > 0 && !getAdditionalInterestCategoriesByIdsWasLoaded) ||
    (purchaseIntentionCategoriesIds.length > 0 && !getPurchaseIntentionCategoriesByIdsWasLoaded)

  useEffect(() => {
    if (isFormOpen && !tikTokRegionsDidLoading && campaignObjective) {
      dispatch(
        getTikTokRegions({
          objective_type: campaignObjective,
          account: tiktokAdAccountId,
          placement: ['PLACEMENT_TIKTOK']
        })
      )
    }
  }, [dispatch, isFormOpen, tiktokAdAccountId, tikTokRegionsDidLoading, campaignObjective])

  useEffect(() => {
    if (isFormOpen && !tikTokAudiencesDidLoading) {
      dispatch(getTikTokCustomAudiences({ account: tiktokAdAccountId, page_size: 999 }))
    }
  }, [dispatch, isFormOpen, tiktokAdAccountId, tikTokAudiencesDidLoading])

  useEffect(() => {
    if (isFormOpen && !tikTokActionCategoriesDidLoading) {
      dispatch(getTikTokActionCategories({ account: tiktokAdAccountId }))
    }
  }, [dispatch, isFormOpen, tiktokAdAccountId, tikTokActionCategoriesDidLoading])

  useEffect(() => {
    if (isFormOpen && !tikTokInterestsDidLoading) {
      dispatch(getTikTokInterests({ account: tiktokAdAccountId, language: 'en', placement: ['PLACEMENT_TIKTOK'] }))
    }
  }, [dispatch, isFormOpen, tiktokAdAccountId, tikTokInterestsDidLoading])

  useEffect(() => {
    if (isFormOpen && additionalInterestCategoriesIds.length > 0) {
      dispatch(
        getAdditionalInterestCategoriesByIds({
          account: tiktokAdAccountId,
          keyword_query: additionalInterestCategoriesIds.map(interestCategoryId => ({
            keyword_id: interestCategoryId,
            language: 'en'
          })),
          filtering: {
            audience_type: 'GENERAL_INTEREST'
          }
        })
      )
    }
  }, [dispatch, isFormOpen, tiktokAdAccountId, campaignObjective, additionalInterestCategoriesIds])

  useEffect(() => {
    if (isFormOpen && purchaseIntentionCategoriesIds.length > 0) {
      dispatch(
        getPurchaseIntentionCategoriesByIds({
          account: tiktokAdAccountId,
          keyword_query: purchaseIntentionCategoriesIds.map(purchaseCategoryId => ({
            keyword_id: purchaseCategoryId,
            language: 'en'
          })),
          filtering: {
            audience_type: 'PURCHASE_INTENTION'
          }
        })
      )
    }
  }, [dispatch, isFormOpen, tiktokAdAccountId, campaignObjective, purchaseIntentionCategoriesIds])

  useEffect(
    () => () => {
      dispatch(clearTikTokRegions())
      dispatch(clearTikTokCustomAudiences())
      dispatch(clearTikTokActionCategories())
      dispatch(clearTikTokInterests())
    },
    [dispatch]
  )

  return isAdditionalDataLoading
}
